import { ImportanceLevel } from "./signal";

export interface SignalFolder {
    id: string;
    title: string;
    description: string;
    /** One short line for the /signals card, read whole (two lines at most). */
    tagline?: string;
    icon: string; // emoji or icon identifier
    color: string; // color scheme for the folder
    eventTypes?: any[]; // filter signals by these event types
    importance?: ImportanceLevel[]; // filter by importance levels
    tags?: string[]; // filter by tags
    isActive?: boolean; // override isActive filter (default: true)
    futureOnly?: boolean; // navigate-only folder: fetches restaurant_opening with signal.date >= today
    anniversaryOnly?: boolean; // folder without signal types: establishments with a 5/6/7/10-year opening anniversary today or within 7 days
    count: number; // number of signals in this folder
    useCases?: string[]; // Use cases/contexts for this signal type
    beta?: boolean; // true = feature in beta, not yet fully available
    standalone?: boolean; // true = opens its own page (/signals/folder/:id) instead of toggling a filter on /signals
}

export const SIGNAL_FOLDERS: SignalFolder[] = [
    {
        id: "restaurant-openings",
        tagline: "Les restaurants qui viennent d'ouvrir et s'équipent.",
        title: "Ouvertures de restaurants",
        description: "Identifiez les nouveaux restaurants et établissements en phase d'ouverture nécessitant équipements et services.",
        icon: "🍽️",
        color: "orange",
        eventTypes: ["restaurant_opening"],
        count: 0,
        useCases: ["Fournisseurs alimentaires", "Équipements cuisine", "Solutions POS", "Services de livraison"]
    },
    {
        id: "future-openings",
        tagline: "À appeler avant leur premier service.",
        title: "Ouvertures à venir",
        description: "Anticipez les restaurants qui ouvriront prochainement et contactez-les avant leur premier service.",
        icon: "📅",
        color: "orange",
        futureOnly: true,
        count: 0,
        useCases: ["Fournisseurs alimentaires", "Équipements cuisine", "Solutions POS", "Services de livraison"]
    },
    {
        id: "restaurant-owner-change",
        tagline: "Un nouvel exploitant reprend et revoit ses fournisseurs.",
        title: "Changement d'exploitant",
        description: "Identifiez les changements d'exploitant dans les restaurants et établissements en phase d'ouverture nécessitant équipements et services.",
        icon: "🍽️",
        color: "orange",
        eventTypes: ["restaurant_owner_change"],
        count: 0,
        useCases: ["Fournisseurs alimentaires", "Équipements cuisine", "Solutions POS", "Services de livraison"]
    },
    {
        id: "new-establishment",
        tagline: "Les gérants de plusieurs établissements.",
        title: "Multi-établissements",
        description: "Identifiez les propriétaires multi-établissements et les opportunités associées.",
        icon: "🏢",
        color: "orange",
        eventTypes: ["multi_site"],
        count: 0,
        useCases: ["Fournisseurs alimentaires", "Équipements cuisine", "Solutions POS", "Services de livraison"]
    },
    {
        id: "anniversaries",
        tagline: "5, 6, 7 ou 10 ans d'ouverture dans les 7 prochains jours.",
        title: "Anniversaires d'ouverture",
        description: "Repérez les établissements qui fêtent leurs 5, 6, 7 ou 10 ans d'ouverture aujourd'hui ou dans les 7 prochains jours.",
        icon: "🎂",
        color: "purple",
        anniversaryOnly: true,
        count: 0,
        useCases: ["Renouvellement d'équipements", "Rénovation", "Offres anniversaire", "Financement"]
    },
    {
        id: "restaurant-closing",
        tagline: "Rachat, liquidation ou reprise du matériel.",
        title: "Fermetures de restaurants",
        description: "Identifiez les restaurants en cours de fermeture pour proposer des solutions de rachat, liquidation ou reprise d'activité.",
        icon: "🔒",
        color: "red",
        eventTypes: ["restaurant_closing"],
        isActive: false,
        count: 0,
        useCases: ["Rachat de fonds de commerce", "Liquidation d'équipements", "Reprise d'activité", "Conseil en cession"]
    },
    {
        id: "hygiene-control",
        tagline: "Les établissements tout juste contrôlés, à accompagner.",
        title: "Contrôles sanitaires",
        description: "Repérez les établissements ayant subi un contrôle sanitaire et identifiez les opportunités d'accompagnement.",
        icon: "🏥",
        color: "red",
        eventTypes: ["hygiene_control"],
        count: 0,
        useCases: ["Solutions d'hygiène", "Formation HACCP", "Équipements sanitaires", "Audit qualité"]
    },
    // {
    //     id: "franchise-signing",
    //     title: "Signature de franchise",
    //     description: "Suivez les nouvelles signatures de franchises et les opportunités de partenariat.",
    //     icon: "📝",
    //     color: "yellow",
    //     eventTypes: ["franchise_signing"],
    //     count: 0,
    //     useCases: ["Conseil en franchise", "Marketing", "Formation", "Support opérationnel"]
    // },
    {
        id: "press-signals",
        tagline: "Ouvertures et reprises vues dans la presse.",
        title: "Signaux presse",
        description: "Détectez les mentions dans la presse locale et nationale pour identifier les établissements en pleine actualité.",
        icon: "📰",
        color: "blue",
        count: 0,
        standalone: true,
        useCases: ["Veille médiatique", "Relations presse", "Opportunités de contact", "Suivi de réputation"]
    },
    // {
    //     id: "fundraising",
    //     title: "Levées de fonds",
    //     description: "Identifiez les entreprises qui viennent de lever des fonds et ont des capacités d'investissement accrues.",
    //     icon: "💰",
    //     color: "blue",
    //     eventTypes: ["seed", "series_a", "series_b", "series_c", "series_d"],
    //     count: 0,
    //     useCases: ["Solutions SaaS", "Services conseil", "Infrastructure IT", "Équipements industriels"]
    // },
    // {
    //     id: "acquisitions",
    //     title: "Acquisitions & Fusions",
    //     description: "Détectez les mouvements stratégiques de consolidation et les opportunités de restructuration.",
    //     icon: "🤝",
    //     color: "purple",
    //     eventTypes: ["acquisition", "merger"],
    //     count: 0,
    //     useCases: ["Services juridiques", "Audit & conseil", "Due diligence", "Gestion de transition"]
    // },
    // {
    //     id: "10-month-ecom",
    //     title: "Lancement projet e-commerce",
    //     description: "Identifiez les entreprises qui lancent un projet e-commerce et nécessitent des solutions digitales. (délais de 10 mois timing ajusté)",
    //     icon: "🛍️ ",
    //     color: "indigo",
    //     eventTypes: ["10-month-ecom"],
    //     count: 0,
    //     useCases: ["Solutions digitales", "Marketing en ligne", "Logistique e-commerce", "Plateformes de paiement"]
    // },
    // {
    //     id: "license-renewal",
    //     title: "Renouvellements de licences",
    //     description: "Identifiez les établissements dont les licences d'exploitation arrivent à échéance et nécessitent un accompagnement.",
    //     icon: "📋",
    //     color: "yellow",
    //     eventTypes: ["license_renewal"],
    //     count: 0,
    //     useCases: ["Services juridiques", "Conseil réglementaire", "Gestion administrative", "Solutions de conformité"]
    // },
    // {
    //     id: "health-inspection",
    //     title: "Contrôles sanitaires",
    //     description: "Repérez les établissements ayant des problèmes de conformité sanitaire nécessitant des solutions d'amélioration.",
    //     icon: "🏥",
    //     color: "red",
    //     eventTypes: ["health_inspection"],
    //     count: 0,
    //     useCases: ["Solutions d'hygiène", "Formation HACCP", "Équipements sanitaires", "Audit qualité"]
    // },
    // {
    //     id: "menu-update",
    //     title: "Mise à jour des cartes",
    //     description: "Suivez les établissements qui renouvellent leur offre et peuvent avoir besoin de nouveaux fournisseurs.",
    //     icon: "📝",
    //     color: "green",
    //     eventTypes: ["menu_update"],
    //     count: 0,
    //     useCases: ["Fournisseurs alimentaires", "Solutions de menu digital", "Conseil culinaire", "Photographie culinaire"]
    // },
    // {
    //     id: "delivery-integration",
    //     title: "Intégration livraison",
    //     description: "Détectez les restaurants qui s'intègrent aux plateformes de livraison et ont des besoins logistiques.",
    //     icon: "🚚",
    //     color: "teal",
    //     eventTypes: ["delivery_integration"],
    //     count: 0,
    //     useCases: ["Solutions POS", "Emballages", "Logistique", "Optimisation des commandes"]
    // },
    // {
    //     id: "franchise-expansion",
    //     title: "Réseaux & Franchises",
    //     description: "Suivez le développement des chaînes de restauration et signatures de nouvelles franchises.",
    //     icon: "🏪",
    //     color: "cyan",
    //     eventTypes: ["franchise_signing", "network_expansion"],
    //     count: 0,
    //     useCases: ["Immobilier commercial", "Aménagement", "Solutions de gestion", "Marketing de réseau"]
    // },
    // {
    //     id: "expansion",
    //     title: "Expansion commerciale",
    //     description: "Repérez les entreprises en phase d'expansion géographique ou de croissance accélérée.",
    //     icon: "📈",
    //     color: "green",
    //     eventTypes: ["expansion"],
    //     importance: ["medium", "high", "critical"],
    //     count: 0,
    //     useCases: ["Équipements professionnels", "Logistique", "Recrutement", "Solutions de paiement"]
    // },
    // {
    //     id: "product-launch",
    //     title: "Lancements produits",
    //     description: "Suivez les lancements de produits et services nécessitant un accompagnement technique ou commercial.",
    //     icon: "📦",
    //     color: "orange",
    //     eventTypes: ["product_launch"],
    //     count: 0,
    //     useCases: ["Marketing digital", "Communication", "Infrastructure cloud", "Services de distribution"]
    // },
    // {
    //     id: "executive-change",
    //     title: "Changements de direction",
    //     description: "Ciblez les entreprises en transition stratégique suite à des changements dans l'équipe dirigeante.",
    //     icon: "👔",
    //     color: "indigo",
    //     eventTypes: ["executive_change"],
    //     importance: ["medium", "high"],
    //     count: 0,
    //     useCases: ["Executive coaching", "Conseil stratégique", "Solutions RH", "Transformation digitale"]
    // },
    // {
    //     id: "partnerships",
    //     title: "Partenariats stratégiques",
    //     description: "Découvrez les nouvelles alliances et collaborations entre entreprises de votre secteur.",
    //     icon: "🔗",
    //     color: "teal",
    //     eventTypes: ["partnership"],
    //     count: 0,
    //     useCases: ["Solutions d'intégration", "Services API", "Co-marketing", "Distribution channel"]
    // },
    // {
    //     id: "ipo",
    //     title: "Introductions en bourse",
    //     description: "Suivez les entreprises en phase de préparation ou d'introduction en bourse (IPO).",
    //     icon: "📊",
    //     color: "red",
    //     eventTypes: ["ipo"],
    //     importance: ["high", "critical"],
    //     count: 0,
    //     useCases: ["Services financiers", "Conformité & réglementation", "Relations investisseurs", "Audit"]
    // },
    // {
    //     id: "high-priority",
    //     title: "Signaux prioritaires",
    //     description: "Tous les signaux critiques nécessitant une attention immédiate, tous secteurs confondus.",
    //     icon: "🔥",
    //     color: "pink",
    //     importance: ["critical"],
    //     count: 0,
    //     useCases: ["Opportunités urgentes", "Deals prioritaires", "Projets à fort impact", "Décisions rapides"]
    // }
];
